/**
 * Send Notification Form Component
 * Form to compose and send test push notifications
 * Supports AI-powered content generation via Agent or direct LLM
 */

import { useState, useEffect, FormEvent } from 'react';
import { Bot, Keyboard, Wand2, Cpu } from 'lucide-react';
import { Card, CardHeader, CardContent, CardFooter } from '@/components/ui/Card';
import { Input } from '@/components/ui/Input';
import { Textarea } from '@/components/ui/Textarea';
import { Button } from '@/components/ui/Button';
import { Select } from '@/components/ui/Select';
import { SystemPromptPopover } from '@/components/common';
import { TargetSelector, TargetSelection } from './TargetSelector';
import { useSendTestNotification, usePushContentAssist, usePushContentTemplate } from '../hooks/usePushNotifications';
import { usePlaygroundAgents } from '@/features/playground/hooks/usePlayground';
import { useOutputSchemasForAgent } from '@/features/system-settings/hooks/useOutputSchemas';
import { useChatModelsQuery } from '@/lib/queries';
import { useI18n } from '@/i18n';
import type { SendTestNotificationRequest } from '@/types/push';

type InputMode = 'agent' | 'llm' | 'manual';

// Predefined data templates for common use cases
const DATA_TEMPLATES = {
  navigation: { type: 'navigation', route: '/(outer-drawer)/(inner-drawer)/' },
  profile: { type: 'action', action: 'profile' },
  feedback: { type: 'action', action: 'feedback' },
  birthChart: { type: 'action', action: 'birth-chart' },
  fortune: { type: 'fortune', route: '/(outer-drawer)/(inner-drawer)/' },
} as const;

export function SendNotificationForm() {
  const { messages } = useI18n();
  const t = messages.pushNotifications;
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [sound, setSound] = useState('default');
  const [badge, setBadge] = useState('');
  const [dataJson, setDataJson] = useState('');
  const [target, setTarget] = useState<TargetSelection>({ type: 'test' });
  const [errors, setErrors] = useState<Record<string, string>>({});

  // Content generation state
  const [inputMode, setInputMode] = useState<InputMode>('manual');
  const [userDescription, setUserDescription] = useState('');
  const [generatingContent, setGeneratingContent] = useState(false);
  // Agent mode state
  const [selectedAgentType, setSelectedAgentType] = useState<string>('');
  const [selectedSchemaKey, setSelectedSchemaKey] = useState<string | null>(null);
  // LLM mode state (empty string to trigger auto-select from available models)
  const [selectedLLMModel, setSelectedLLMModel] = useState<string>('');
  const [llmTemperature, setLLMTemperature] = useState<number>(0.7);
  // Store last response's prompts for transparency display
  const [lastSystemPrompt, setLastSystemPrompt] = useState<string | undefined>();
  const [lastUserPrompt, setLastUserPrompt] = useState<string | undefined>();

  const sendMutation = useSendTestNotification();
  const contentAssistMutation = usePushContentAssist();

  // Fetch system prompt template for LLM mode
  const { data: promptTemplate } = usePushContentTemplate();

  // Fetch agents list
  const { data: agents = [], isLoading: agentsLoading } = usePlaygroundAgents();

  // Fetch output schemas for selected agent from centralized API
  const { data: schemasData, isLoading: schemasLoading } = useOutputSchemasForAgent(selectedAgentType || undefined);
  const agentSchemas = schemasData?.schemas || [];

  // Fetch chat models for LLM mode
  const { data: chatModels = [], isLoading: chatModelsLoading } = useChatModelsQuery();

  // Auto-select first agent when agents load
  useEffect(() => {
    if (agents.length > 0 && !selectedAgentType) {
      setSelectedAgentType(agents[0].agent_type);
    }
  }, [agents, selectedAgentType]);

  // Reset schema when agent changes
  useEffect(() => {
    // Reset schema selection when agent changes
    setSelectedSchemaKey(null);
  }, [selectedAgentType]);

  // Auto-select first chat model when models load
  useEffect(() => {
    if (chatModels.length > 0 && !selectedLLMModel) {
      setSelectedLLMModel(chatModels[0].id);
    }
  }, [chatModels, selectedLLMModel]);

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!title.trim()) {
      newErrors.title = t.validation.titleRequired;
    }

    if (!body.trim()) {
      newErrors.body = t.validation.bodyRequired;
    }

    if (target.type === 'user' && (!target.user_ids || target.user_ids.length === 0)) {
      newErrors.target = t.validation.userIdsRequired;
    }

    if (dataJson.trim()) {
      try {
        JSON.parse(dataJson);
      } catch (e) {
        newErrors.dataJson = t.validation.invalidJson;
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    const request: SendTestNotificationRequest = {
      title: title.trim(),
      body: body.trim(),
      target,
      sound: sound || undefined,
      badge: badge ? parseInt(badge, 10) : undefined,
      data: dataJson.trim() ? JSON.parse(dataJson) : undefined,
    };

    try {
      const result = await sendMutation.mutateAsync(request);

      // Show success message
      const failedDetails = result.details?.filter(d => d.status === 'failed') || [];
      alert(
        `${t.sendForm.successMessage}\n\n` +
        `${t.sendForm.sent}: ${result.sent_count}\n` +
        `${t.sendForm.failed}: ${result.failed_count}` +
        (failedDetails.length > 0
          ? `\n\n${t.sendForm.errors}:\n${failedDetails.map(d => d.message || 'Unknown error').join('\n')}`
          : '')
      );

      // Reset form on success
      setTitle('');
      setBody('');
      setSound('default');
      setBadge('');
      setDataJson('');
      setTarget({ type: 'test' });
    } catch (error) {
      console.error('Failed to send notification:', error);
      alert(`Failed to send notification: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  };

  // Generate push notification content using AI (Agent or LLM mode)
  const handleGenerateContent = async () => {
    if (!userDescription.trim()) {
      return;
    }

    setGeneratingContent(true);
    try {
      if (inputMode === 'agent') {
        // Agent Mode
        if (!selectedAgentType) {
          alert(t.contentAssist?.selectAgentFirst ?? 'Please select an agent first');
          setGeneratingContent(false);
          return;
        }

        const response = await contentAssistMutation.mutateAsync({
          user_input: userDescription,
          agent_type: selectedAgentType,
          output_schema_key: selectedSchemaKey || undefined,
          language: 'zh',
        });

        setTitle(response.title);
        setBody(response.body);
      } else if (inputMode === 'llm') {
        // LLM Mode
        if (!selectedLLMModel) {
          alert(t.contentAssist?.selectModelFirst ?? 'Please select an LLM model first');
          setGeneratingContent(false);
          return;
        }

        const response = await contentAssistMutation.mutateAsync({
          user_input: userDescription,
          model: selectedLLMModel,
          temperature: llmTemperature,
          language: 'zh',
        });

        setTitle(response.title);
        setBody(response.body);
        // Save prompts for transparency display
        setLastSystemPrompt(response.system_prompt);
        setLastUserPrompt(response.user_prompt);
      }
    } catch (error) {
      console.error('Failed to generate content:', error);
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      alert(`${t.contentAssist?.generateFailed ?? 'Content generation failed'}: ${errorMessage}`);
    } finally {
      setGeneratingContent(false);
    }
  };

  return (
    <Card>
      <CardHeader
        title={t.sendForm.title}
        description={t.sendForm.description}
      />

      <form onSubmit={handleSubmit}>
        <CardContent className="space-y-4">
          {/* Content Generation Section - Grouped together */}
          <div className="p-4 border-2 border-gray-200 rounded-lg space-y-4">
            <div className="flex items-center gap-2 text-sm font-medium text-gray-700 border-b border-gray-200 pb-2 mb-2">
              <Wand2 className="w-4 h-4" />
              <span>{t.contentAssist?.sectionTitle ?? '推送内容'}</span>
            </div>

            {/* Input Mode Toggle - Three modes: Agent, LLM, Manual */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t.contentAssist?.inputModeLabel ?? '内容输入方式'}
              </label>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => setInputMode('agent')}
                  className={`flex-1 py-2 px-3 rounded-lg border-2 text-sm font-medium transition-all flex items-center justify-center gap-2 ${
                    inputMode === 'agent'
                      ? 'bg-primary-100 border-primary-500 text-primary-700'
                      : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  <Bot className="w-4 h-4" />
                  <span>{t.contentAssist?.agentMode ?? 'Agent'}</span>
                </button>
                <button
                  type="button"
                  onClick={() => setInputMode('llm')}
                  className={`flex-1 py-2 px-3 rounded-lg border-2 text-sm font-medium transition-all flex items-center justify-center gap-2 ${
                    inputMode === 'llm'
                      ? 'bg-primary-100 border-primary-500 text-primary-700'
                      : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  <Cpu className="w-4 h-4" />
                  <span>{t.contentAssist?.llmMode ?? 'LLM'}</span>
                </button>
                <button
                  type="button"
                  onClick={() => setInputMode('manual')}
                  className={`flex-1 py-2 px-3 rounded-lg border-2 text-sm font-medium transition-all flex items-center justify-center gap-2 ${
                    inputMode === 'manual'
                      ? 'bg-primary-100 border-primary-500 text-primary-700'
                      : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  <Keyboard className="w-4 h-4" />
                  <span>{t.contentAssist?.manualMode ?? '手动'}</span>
                </button>
              </div>
            </div>

            {/* Agent-Assisted Input */}
            {inputMode === 'agent' && (
              <div className="space-y-4 p-4 bg-gray-50 rounded-lg border border-gray-200">
                {/* Agent Selection */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t.contentAssist?.selectAgent ?? '选择 Agent'}
                    {agentsLoading && <span className="ml-2 text-gray-400 text-xs">{t.contentAssist?.loading ?? '加载中...'}</span>}
                  </label>
                  <select
                    value={selectedAgentType}
                    onChange={(e) => setSelectedAgentType(e.target.value)}
                    disabled={agentsLoading || agents.length === 0}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-primary-500 focus:border-primary-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
                  >
                    {agents.length === 0 && !agentsLoading && (
                      <option value="">{t.contentAssist?.noAgents ?? '无可用 Agent'}</option>
                    )}
                    {agents.map((agent) => (
                      <option key={agent.agent_type} value={agent.agent_type}>
                        {agent.name} ({agent.agent_type})
                      </option>
                    ))}
                  </select>
                  {selectedAgentType && agents.find(a => a.agent_type === selectedAgentType)?.description && (
                    <p className="mt-1 text-xs text-gray-500">
                      {agents.find(a => a.agent_type === selectedAgentType)?.description}
                    </p>
                  )}
                </div>

                {/* Output Schema Selector */}
                {selectedAgentType && (
                  <div className="space-y-2">
                    <label className="flex items-center gap-2 text-sm font-medium text-gray-700">
                      <span>输出格式 (Output Schema)</span>
                      {schemasLoading && <span className="text-gray-400 text-xs">加载中...</span>}
                    </label>
                    <select
                      value={selectedSchemaKey || 'none'}
                      onChange={(e) => {
                        const value = e.target.value;
                        setSelectedSchemaKey(value === 'none' ? null : value);
                      }}
                      disabled={schemasLoading || agentSchemas.length === 0}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white focus:ring-primary-500 focus:border-primary-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
                    >
                      <option value="none">无格式约束 (自由输出)</option>
                      {agentSchemas.map((schema) => (
                        <option key={schema.key} value={schema.key}>
                          {schema.name_zh || schema.name} ({schema.key})
                        </option>
                      ))}
                    </select>
                    {selectedSchemaKey && agentSchemas.find(s => s.key === selectedSchemaKey)?.description && (
                      <p className="text-xs text-gray-500">
                        {agentSchemas.find(s => s.key === selectedSchemaKey)?.description}
                      </p>
                    )}
                    {!selectedSchemaKey && agentSchemas.length > 0 && (
                      <p className="text-xs text-gray-400">
                        选择格式后，Agent 会按指定的 JSON 结构输出
                      </p>
                    )}
                  </div>
                )}

                {/* User Description */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t.contentAssist?.descriptionLabel ?? '描述推送内容'}
                  </label>
                  <Textarea
                    value={userDescription}
                    onChange={(e) => setUserDescription(e.target.value)}
                    rows={2}
                    placeholder={t.contentAssist?.descriptionPlaceholder ?? '例如：提醒用户查看今日运势'}
                  />
                </div>

                {/* Generate Button */}
                <button
                  type="button"
                  onClick={handleGenerateContent}
                  disabled={generatingContent || !userDescription.trim() || !selectedAgentType}
                  className="w-full px-4 py-2.5 bg-primary-600 text-white rounded-lg font-medium hover:bg-primary-700 disabled:opacity-50 transition-colors flex items-center justify-center gap-2"
                >
                  <Wand2 className={`w-4 h-4 ${generatingContent ? 'animate-spin' : ''}`} />
                  <span>{generatingContent ? (t.contentAssist?.generating ?? '生成中...') : (t.contentAssist?.generateButton ?? '生成内容')}</span>
                </button>
              </div>
            )}

            {/* LLM Mode Input */}
            {inputMode === 'llm' && (
              <div className="space-y-4 p-4 bg-gray-50 rounded-lg border border-gray-200">
                {/* System Prompt Template Preview */}
                {promptTemplate?.system_prompt?.zh && (
                  <div className="border border-gray-200 rounded-lg overflow-hidden">
                    <div className="px-3 py-2 bg-gray-100 border-b border-gray-200 flex items-center justify-between">
                      <span className="text-xs font-medium text-gray-600">System Prompt 模板</span>
                      {lastUserPrompt && (
                        <SystemPromptPopover
                          systemPrompt={lastSystemPrompt}
                          userPrompt={lastUserPrompt}
                          iconSize={14}
                          className="ml-2"
                        />
                      )}
                    </div>
                    <pre className="p-3 text-xs text-gray-600 whitespace-pre-wrap font-mono bg-white max-h-32 overflow-y-auto">
                      {promptTemplate.system_prompt.zh}
                    </pre>
                  </div>
                )}

                {/* LLM Model Selection */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t.contentAssist?.selectLLMModel ?? '选择 LLM 模型'}
                    {chatModelsLoading && <span className="ml-2 text-gray-400 text-xs">{t.contentAssist?.loading ?? '加载中...'}</span>}
                  </label>
                  <select
                    value={selectedLLMModel}
                    onChange={(e) => setSelectedLLMModel(e.target.value)}
                    disabled={chatModelsLoading || chatModels.length === 0}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-primary-500 focus:border-primary-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
                  >
                    {chatModels.length === 0 && !chatModelsLoading && (
                      <option value="">{t.contentAssist?.noModels ?? '无可用模型'}</option>
                    )}
                    {chatModels.map((model) => (
                      <option key={model.id} value={model.id}>
                        {model.id}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Temperature Slider */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t.contentAssist?.temperature ?? '温度 (Temperature)'}: {llmTemperature.toFixed(1)}
                  </label>
                  <input
                    type="range"
                    min="0"
                    max="2"
                    step="0.1"
                    value={llmTemperature}
                    onChange={(e) => setLLMTemperature(parseFloat(e.target.value))}
                    className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-primary-600"
                  />
                  <div className="flex justify-between text-xs text-gray-500 mt-1">
                    <span>{t.contentAssist?.precise ?? '精确 (0)'}</span>
                    <span>{t.contentAssist?.balanced ?? '平衡 (1)'}</span>
                    <span>{t.contentAssist?.creative ?? '创意 (2)'}</span>
                  </div>
                </div>

                {/* User Description */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t.contentAssist?.descriptionLabel ?? '描述推送内容'}
                  </label>
                  <Textarea
                    value={userDescription}
                    onChange={(e) => setUserDescription(e.target.value)}
                    rows={2}
                    placeholder={t.contentAssist?.descriptionPlaceholder ?? '例如：提醒用户查看今日运势'}
                  />
                </div>

                {/* Generate Button */}
                <button
                  type="button"
                  onClick={handleGenerateContent}
                  disabled={generatingContent || !userDescription.trim() || !selectedLLMModel}
                  className="w-full px-4 py-2.5 bg-primary-600 text-white rounded-lg font-medium hover:bg-primary-700 disabled:opacity-50 transition-colors flex items-center justify-center gap-2"
                >
                  <Wand2 className={`w-4 h-4 ${generatingContent ? 'animate-spin' : ''}`} />
                  <span>{generatingContent ? (t.contentAssist?.generating ?? '生成中...') : (t.contentAssist?.generateButton ?? '生成内容')}</span>
                </button>

                {/* Show actual prompts after generation */}
                {lastUserPrompt && (
                  <div className="border border-blue-200 rounded-lg overflow-hidden bg-blue-50">
                    <div className="px-3 py-2 bg-blue-100 border-b border-blue-200">
                      <span className="text-xs font-medium text-blue-700">实际发送的 User Prompt</span>
                    </div>
                    <pre className="p-3 text-xs text-blue-800 whitespace-pre-wrap font-mono max-h-24 overflow-y-auto">
                      {lastUserPrompt}
                    </pre>
                  </div>
                )}
              </div>
            )}

            {/* Title and Body Inputs */}
            <Input
              label={t.sendForm.notificationTitle}
              placeholder={t.sendForm.notificationTitlePlaceholder}
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              error={errors.title}
              required
            />

            <Textarea
              label={t.sendForm.body}
              placeholder={t.sendForm.bodyPlaceholder}
              value={body}
              onChange={(e) => setBody(e.target.value)}
              error={errors.body}
              rows={3}
              required
            />
          </div>

          <TargetSelector
            value={target}
            onChange={setTarget}
            error={errors.target}
          />

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Select
              label={t.sendForm.sound}
              value={sound}
              onChange={(e) => setSound(e.target.value)}
              options={[
                { value: 'default', label: t.sendForm.soundDefault ?? 'Default' },
                { value: 'none', label: t.sendForm.soundNone ?? 'None (Silent)' },
              ]}
              helperText={t.sendForm.soundHint}
            />

            <Input
              label={t.sendForm.badgeCount}
              type="number"
              placeholder={t.sendForm.badgeCountPlaceholder}
              value={badge}
              onChange={(e) => setBadge(e.target.value)}
              helperText={t.sendForm.badgeCountHintFull ?? '0 = Clear badge, empty = No change'}
              min="0"
            />
          </div>

          {/* Custom Data Section */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <label className="block text-sm font-medium text-gray-700">
                {t.sendForm.customData}
              </label>
              {/* Data Template Quick Buttons */}
              <div className="flex gap-1 flex-wrap">
                <span className="text-xs text-gray-500 mr-1">{t.sendForm.dataTemplates ?? 'Templates'}:</span>
                {Object.entries(DATA_TEMPLATES).map(([key, template]) => (
                  <button
                    key={key}
                    type="button"
                    onClick={() => setDataJson(JSON.stringify(template, null, 2))}
                    className="px-2 py-0.5 text-xs bg-gray-100 hover:bg-gray-200 text-gray-700 rounded transition-colors"
                  >
                    {t.sendForm.dataTemplateLabels?.[key as keyof typeof DATA_TEMPLATES] ?? key}
                  </button>
                ))}
              </div>
            </div>
            <Textarea
              placeholder={t.sendForm.customDataPlaceholder}
              value={dataJson}
              onChange={(e) => setDataJson(e.target.value)}
              error={errors.dataJson}
              rows={3}
              helperText={t.sendForm.customDataHint}
            />
          </div>

          {sendMutation.isError && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-sm text-red-800">
                <strong>Error:</strong> {sendMutation.error instanceof Error ? sendMutation.error.message : 'Failed to send notification'}
              </p>
            </div>
          )}
        </CardContent>

        <CardFooter>
          <div className="flex justify-end gap-3">
            <Button
              type="button"
              variant="secondary"
              onClick={() => {
                setTitle('');
                setBody('');
                setSound('default');
                setBadge('');
                setDataJson('');
                setTarget({ type: 'test' });
                setErrors({});
              }}
              disabled={sendMutation.isPending}
            >
              {t.sendForm.clearButton}
            </Button>
            <Button
              type="submit"
              loading={sendMutation.isPending}
              disabled={sendMutation.isPending}
            >
              {sendMutation.isPending ? t.sendForm.sending : t.sendForm.sendButton}
            </Button>
          </div>
        </CardFooter>
      </form>
    </Card>
  );
}
